import React, { useState, useMemo } from "react";
import type { TransactionAttributes } from "../../../types";
import type { BankAccountAttributes } from "../../../types";
import type { CostCenterAttributes } from "../../../types";
import type { SubcategoryAttributes } from "../../../types";
import {
  ActionButton,
  Card,
  DataList,
  EmptyState,
  IconsLarge,
  ListColumn,
  ListAction,
  Input,
  Modal,
  Select,
  Icons,
} from "../../shared";
import type { SelectOption } from "../../shared";
import { formatCurrency, formatDate, formatMonthYear } from "../../../utils/formatUtils";
import {
  createDateInBrazil,
  getBrazilCalendarMonthRange,
  getTodayInBrazil,
  TIMEZONE,
} from "../../../utils/dateUtils";

/** YYYY-MM-DD no calendário de São Paulo, para comparar com getTodayInBrazil(). */
function transactionDateKeyBrazil(dateStr: string | null | undefined): string | null {
  if (!dateStr?.trim()) return null;
  const raw = dateStr.trim();
  const ymd = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (ymd) {
    try {
      const d = createDateInBrazil(ymd[1]);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    } catch {
      return null;
    }
  }
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-CA", { timeZone: TIMEZONE });
  } catch {
    return null;
  }
}

type PaymentSituationFilter = "" | "paid" | "overdue" | "future";

function transactionPaymentSituation(t: TransactionAttributes, todayBrazil: string): PaymentSituationFilter {
  if (t.paymentDate?.trim()) return "paid";
  const dueKey = transactionDateKeyBrazil(t.dueDate);
  if (dueKey && dueKey < todayBrazil) return "overdue";
  return "future";
}

const PAYMENT_SITUATION_OPTIONS: SelectOption[] = [
  { value: "", label: "Todos" },
  { value: "paid", label: "Pago" },
  { value: "overdue", label: "Atrasado" },
  { value: "future", label: "Futuro" },
];

export interface DateFilterState {
  dateFilterType: DateFilterType;
  dateRangeFrom: string;
  dateRangeTo: string;
}

interface TransactionsListViewProps {
  transactions: TransactionAttributes[];
  subcategories: SubcategoryAttributes[];
  bankAccounts: BankAccountAttributes[];
  costCenters: CostCenterAttributes[];
  /** ID do lançamento — a página pai busca com `GET /transactions/:id` antes de abrir o modal. */
  onEdit: (transactionId: number) => void;
  onDelete: (id: number) => void;
  /** Ações em massa na barra abaixo do cabeçalho da tabela (quando há linhas selecionadas). */
  onBulkEdit?: () => void;
  onBulkDelete?: () => void;
  loading?: boolean;
  /** Controlled selection (transaction IDs). When provided with onSelectionChange, parent owns selection. */
  selectedTransactionIds?: (string | number)[];
  onSelectionChange?: (selectedKeys: (string | number)[]) => void;
  /** Date filter (sent to backend). When provided, list is controlled. */
  dateFilterType?: DateFilterType;
  dateRangeFrom?: string;
  dateRangeTo?: string;
  onDateFilterChange?: (filter: DateFilterState) => void;
  /** Refetch ao clicar em Aplicar no filtro de período (ou Limpar período). */
  onDateFilterApply?: (filter: DateFilterState) => void;
  /** Botão no empty state quando a API não retornou lançamentos. */
  onEmptyCreateOutput?: () => void;
}

const byId = <T extends { id: number }>(arr: T[]) => (id: number) =>
  arr.find((x) => x.id === id);

const TYPE_OPTIONS: SelectOption[] = [
  { value: "", label: "Todos" },
  { value: "input", label: "Entrada" },
  { value: "output", label: "Saída" },
];

type DateFilterType = "competence" | "due" | "payment";

const DATE_FILTER_OPTIONS: { value: DateFilterType; label: string }[] = [
  { value: "competence", label: "Competência" },
  { value: "due", label: "Vencimento" },
  { value: "payment", label: "Pagamento" },
];

export const TransactionsListView: React.FC<TransactionsListViewProps> = ({
  transactions,
  subcategories,
  bankAccounts,
  costCenters,
  onEdit,
  onDelete,
  onBulkEdit,
  onBulkDelete,
  loading = false,
  selectedTransactionIds: controlledSelectedIds,
  onSelectionChange,
  dateFilterType: controlledDateFilterType,
  dateRangeFrom: controlledDateRangeFrom,
  dateRangeTo: controlledDateRangeTo,
  onDateFilterChange,
  onDateFilterApply,
  onEmptyCreateOutput,
}) => {
  /** Só o período fica em rascunho até "Aplicar filtros" (refetch na API). Colunas aplicam na hora e permanecem ao fechar o modal. */
  const [filterModal, setFilterModal] = useState<{ open: boolean; periodDraft: DateFilterState | null }>({
    open: false,
    periodDraft: null,
  });
  const [descriptionFilter, setDescriptionFilter] = useState("");
  const [subcategoryIdFilter, setSubcategoryIdFilter] = useState<string | number>("");
  const [bankAccountIdFilter, setBankAccountIdFilter] = useState<string | number>("");
  const [costCenterIdFilter, setCostCenterIdFilter] = useState<string | number>("");
  const [typeFilter, setTypeFilter] = useState<string | number>("");
  const [paymentSituationFilter, setPaymentSituationFilter] = useState<PaymentSituationFilter>("");
  const [internalDateFilterType, setInternalDateFilterType] = useState<DateFilterType>("competence");
  const [internalDateRangeFrom, setInternalDateRangeFrom] = useState(
    () => getBrazilCalendarMonthRange().from
  );
  const [internalDateRangeTo, setInternalDateRangeTo] = useState(() => getBrazilCalendarMonthRange().to);
  const isDateFilterControlled =
    controlledDateFilterType != null &&
    controlledDateRangeFrom != null &&
    controlledDateRangeTo != null &&
    onDateFilterChange != null;
  const dateFilterType = isDateFilterControlled ? controlledDateFilterType : internalDateFilterType;
  const dateRangeFrom = isDateFilterControlled ? controlledDateRangeFrom : internalDateRangeFrom;
  const dateRangeTo = isDateFilterControlled ? controlledDateRangeTo : internalDateRangeTo;
  const setDateFilter = (next: DateFilterState) => {
    if (isDateFilterControlled) onDateFilterChange?.(next);
    else {
      setInternalDateFilterType(next.dateFilterType);
      setInternalDateRangeFrom(next.dateRangeFrom);
      setInternalDateRangeTo(next.dateRangeTo);
    }
  };

  const [periodFilterError, setPeriodFilterError] = useState<string | null>(null);

  const openFiltersModal = () => {
    setPeriodFilterError(null);
    setFilterModal({
      open: true,
      periodDraft: {
        dateFilterType,
        dateRangeFrom,
        dateRangeTo,
      },
    });
  };

  const closeFiltersModal = () => {
    setPeriodFilterError(null);
    setFilterModal({ open: false, periodDraft: null });
  };

  const patchPeriodDraft = (patch: Partial<DateFilterState>) => {
    setFilterModal((m) =>
      m.periodDraft ? { ...m, periodDraft: { ...m.periodDraft, ...patch } } : m
    );
  };

  const handleClearPeriodDraft = () => {
    patchPeriodDraft({ dateRangeFrom: "", dateRangeTo: "" });
    setPeriodFilterError(null);
  };

  const applyFiltersModal = () => {
    const d = filterModal.periodDraft;
    if (!d) return;
    const f = d.dateRangeFrom.trim();
    const t = d.dateRangeTo.trim();
    if (f && !t) {
      setPeriodFilterError("Preencha também a data final (Até).");
      return;
    }
    if (!f && t) {
      setPeriodFilterError("Preencha também a data inicial (De).");
      return;
    }
    setPeriodFilterError(null);
    setDateFilter({
      dateFilterType: d.dateFilterType,
      dateRangeFrom: d.dateRangeFrom,
      dateRangeTo: d.dateRangeTo,
    });
    onDateFilterApply?.({
      dateFilterType: d.dateFilterType,
      dateRangeFrom: d.dateRangeFrom,
      dateRangeTo: d.dateRangeTo,
    });
    setFilterModal({ open: false, periodDraft: null });
  };

  const [internalSelectedIds, setInternalSelectedIds] = useState<(string | number)[]>([]);
  const isSelectionControlled = controlledSelectedIds != null && onSelectionChange != null;
  const selectedTransactionIds = isSelectionControlled ? controlledSelectedIds : internalSelectedIds;
  const setSelectedTransactionIds = (keys: (string | number)[]) => {
    if (isSelectionControlled) onSelectionChange?.(keys);
    else setInternalSelectedIds(keys);
  };

  const getSubcategory = byId(subcategories);
  const getBankAccount = byId(bankAccounts);
  const getCostCenter = byId(costCenters);

  const subcategoryOptions: SelectOption[] = [
    { value: "", label: "Todas" },
    ...subcategories.map((s) => ({ value: s.id, label: s.name })),
  ];
  const bankAccountOptions: SelectOption[] = [
    { value: "", label: "Todas" },
    ...bankAccounts.map((a) => ({ value: a.id, label: a.name })),
  ];
  const costCenterOptions: SelectOption[] = [
    { value: "", label: "Todos" },
    ...costCenters.map((c) => ({ value: c.id, label: c.name })),
  ];

  const filteredTransactions = useMemo(() => {
    const todayBrazil = getTodayInBrazil();
    return transactions.filter((t) => {
      if (descriptionFilter.trim()) {
        const desc = (t.description ?? "").toLowerCase();
        if (!desc.includes(descriptionFilter.trim().toLowerCase())) return false;
      }
      if (subcategoryIdFilter !== "" && subcategoryIdFilter !== undefined) {
        const id =
          typeof subcategoryIdFilter === "number" ? subcategoryIdFilter : Number(subcategoryIdFilter);
        if (!Number.isNaN(id) && Number(t.subcategoryId) !== id) return false;
      }
      if (bankAccountIdFilter !== "" && bankAccountIdFilter !== undefined) {
        const id =
          typeof bankAccountIdFilter === "number" ? bankAccountIdFilter : Number(bankAccountIdFilter);
        if (!Number.isNaN(id) && Number(t.bankAccountId) !== id) return false;
      }
      if (costCenterIdFilter !== "" && costCenterIdFilter !== undefined) {
        const id =
          typeof costCenterIdFilter === "number" ? costCenterIdFilter : Number(costCenterIdFilter);
        if (!Number.isNaN(id) && Number(t.costCenterId ?? NaN) !== id) return false;
      }
      if (typeFilter === "input" || typeFilter === "output") {
        if (t.type !== typeFilter) return false;
      }
      if (
        paymentSituationFilter === "paid" ||
        paymentSituationFilter === "overdue" ||
        paymentSituationFilter === "future"
      ) {
        if (transactionPaymentSituation(t, todayBrazil) !== paymentSituationFilter) return false;
      }
      return true;
    });
  }, [
    transactions,
    descriptionFilter,
    subcategoryIdFilter,
    bankAccountIdFilter,
    costCenterIdFilter,
    typeFilter,
    paymentSituationFilter,
  ]);

  const columns: ListColumn<TransactionAttributes>[] = [
    {
      key: "scheduleMarkers",
      label: "\u00A0",
      /** DataList: `minmax(72px, width)` — max ≥ 72px. Ícones: parcelamento + recorrência. */
      width: "100px",
      className: "justify-center",
      render: (t) => {
        const hasInstallment = Boolean(t.installment?.trim());
        const hasRecurrency = t.recurrencyId != null && t.recurrencyId !== 0;
        if (!hasInstallment && !hasRecurrency) {
          return <span className="inline-block w-[18px]" aria-hidden />;
        }
        return (
          <span className="inline-flex items-center justify-center gap-1.5">
            {hasInstallment ? (
              <span
                className="inline-flex text-amber-600 dark:text-[#63B3ED]"
                title={`Parcelamento (${t.installment?.trim()})`}
                aria-label="Transação parcelada"
              >
                <Icons.Installment />
              </span>
            ) : null}
            {hasRecurrency ? (
              <span
                className="inline-flex text-sky-600 dark:text-[#63B3ED]"
                title={`Recorrência (grupo #${t.recurrencyId})`}
                aria-label="Transação de recorrência"
              >
                <Icons.Recurrency />
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: "subcategory",
      label: "Categoria",
      width: "0.55fr",
      render: (t) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {getSubcategory(t.subcategoryId)?.name ?? `#${t.subcategoryId}`}
        </span>
      ),
    },
    {
      key: "competenceDate",
      label: "Data de competência",
      width: "0.43fr",
      render: (t) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {formatMonthYear(t.competenceDate)}
        </span>
      ),
    },
    {
      key: "dueDate",
      label: "Data de vencimento",
      width: "0.43fr",
      render: (t) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {formatDate(t.dueDate)}
        </span>
      ),
    },
    {
      key: "paymentDate",
      label: "Data de pagamento",
      width: "0.43fr",
      render: (t) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {formatDate(t.paymentDate)}
        </span>
      ),
    },
    {
      key: "amount",
      label: "Valor",
      width: "0.45fr",
      render: (t) => (
        <span
          className={`text-sm font-semibold ${
            t.type === "input" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
          }`}
        >
          {t.type === "input" ? "+" : "-"}
          {formatCurrency(t.amount)}
        </span>
      ),
    },
    {
      key: "bankAccount",
      label: "Portadores",
      width: "0.45fr",
      render: (t) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">
          {getBankAccount(t.bankAccountId)?.name ?? `#${t.bankAccountId}`}
        </span>
      ),
    },
    {
      key: "descriptionProduct",
      label: "Descrição\nProduto ou serviço",
      /** DataList já envolve em `minmax(72px, …)` — usar só `fr` aqui. */
      width: "1.35fr",
      className: "min-w-0 self-start whitespace-pre-line leading-tight",
      render: (t) => (
        <div className="flex flex-col gap-0.5 py-0.5 min-w-0 w-full">
          <span className="text-sm text-gray-900 dark:text-slate-200 font-medium leading-snug">
            {t.description || "—"}
          </span>
          <span className="text-xs text-gray-500 dark:text-slate-400 leading-snug">
            {t.product || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "observationCostCenter",
      label: "Observação\nCentro de custo",
      width: "1.05fr",
      className: "min-w-0 self-start whitespace-pre-line leading-tight",
      render: (t) => (
        <div className="flex flex-col gap-0.5 py-0.5 min-w-0 w-full">
          <span className="text-sm text-gray-600 dark:text-slate-400 leading-snug">
            {t.observation || "—"}
          </span>
          <span className="text-xs text-gray-500 dark:text-slate-400 leading-snug">
            {t.costCenterId == null
              ? "—"
              : getCostCenter(t.costCenterId)?.name ?? `#${t.costCenterId}`}
          </span>
        </div>
      ),
    },
    {
      key: "paymentMethod",
      label: "Forma de pagamento",
      width: "0.5fr",
      render: (t) => (
        <span className="text-sm text-gray-600 dark:text-slate-400">{t.paymentMethod || "—"}</span>
      ),
    },
    {
      key: "installment",
      label: "Parcelas",
      width: "0.38fr",
      render: (t) => {
        const v = t.installment?.trim();
        return (
          <span className="text-sm text-gray-600 dark:text-slate-400 tabular-nums">
            {v ? v : "—"}
          </span>
        );
      },
    },
  ];

  const actions: ListAction<TransactionAttributes>[] = [
    {
      label: "Editar",
      icon: <Icons.Edit />,
      onClick: (t) => onEdit(t.id),
      className:
        "p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200",
      title: "Editar transação",
      disabled: () => loading,
    },
    {
      label: "Excluir",
      icon: <Icons.Delete />,
      onClick: (t) => onDelete(t.id),
      className:
        "p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200",
      title: "Excluir transação",
      disabled: () => loading,
    },
  ];

  const defaultCompetenceMonth = getBrazilCalendarMonthRange();
  const periodDiffersFromDefaultCompetenceMonth =
    dateFilterType !== "competence" ||
    dateRangeFrom.trim() !== defaultCompetenceMonth.from ||
    dateRangeTo.trim() !== defaultCompetenceMonth.to;

  const hasActiveFilters =
    descriptionFilter.trim() ||
    subcategoryIdFilter !== "" ||
    bankAccountIdFilter !== "" ||
    costCenterIdFilter !== "" ||
    typeFilter !== "" ||
    paymentSituationFilter !== "" ||
    periodDiffersFromDefaultCompetenceMonth;

  const clearFilters = () => {
    setDescriptionFilter("");
    setSubcategoryIdFilter("");
    setBankAccountIdFilter("");
    setCostCenterIdFilter("");
    setTypeFilter("");
    setPaymentSituationFilter("");
    const nextMonth = getBrazilCalendarMonthRange();
    const clearedDate = {
      dateFilterType: "competence" as const,
      dateRangeFrom: nextMonth.from,
      dateRangeTo: nextMonth.to,
    };
    setDateFilter(clearedDate);
    onDateFilterApply?.(clearedDate);
    setFilterModal({ open: false, periodDraft: null });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 gap-y-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
          <ActionButton
            type="button"
            variant="outline"
            size="sm"
            icon={<Icons.Search />}
            onClick={openFiltersModal}
            disabled={loading}
            className="shrink-0 dark:border-[#4B5563] dark:bg-[#374151] dark:text-slate-200 dark:hover:bg-[#2D3748]"
          >
            Filtros
          </ActionButton>
          {hasActiveFilters && (
            <>
              <ActionButton type="button" variant="link" onClick={clearFilters}>
                Limpar filtros
              </ActionButton>
              <span className="text-xs text-gray-500 dark:text-slate-400 tabular-nums">
                {filteredTransactions.length} de {transactions.length} transações
              </span>
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={filterModal.open}
        onClose={closeFiltersModal}
        title="Filtrar transações"
        icon={<Icons.Search />}
        size="lg"
        headerGradient="gray"
      >
        {filterModal.open && filterModal.periodDraft && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Descrição
                </label>
                <Input
                  type="text"
                  value={descriptionFilter}
                  onChange={(e) => setDescriptionFilter(e.target.value)}
                  placeholder="Buscar..."
                  disabled={loading}
                  className="text-sm"
                />
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Categoria
                </label>
                <Select
                  value={subcategoryIdFilter}
                  onChange={(v) => setSubcategoryIdFilter(v)}
                  options={subcategoryOptions}
                  placeholder="Todas"
                  disabled={loading}
                  searchable={subcategoryOptions.length > 8}
                  className="text-sm"
                />
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Conta
                </label>
                <Select
                  value={bankAccountIdFilter}
                  onChange={(v) => setBankAccountIdFilter(v)}
                  options={bankAccountOptions}
                  placeholder="Todas"
                  disabled={loading}
                  searchable={bankAccountOptions.length > 8}
                  className="text-sm"
                />
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Centro de custo
                </label>
                <Select
                  value={costCenterIdFilter}
                  onChange={(v) => setCostCenterIdFilter(v)}
                  options={costCenterOptions}
                  placeholder="Todos"
                  disabled={loading}
                  searchable={costCenterOptions.length > 8}
                  className="text-sm"
                />
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Tipo
                </label>
                <Select
                  value={typeFilter}
                  onChange={(v) => setTypeFilter(v)}
                  options={TYPE_OPTIONS}
                  disabled={loading}
                  searchable={false}
                  className="text-sm"
                />
              </div>
              <div className="min-w-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                  Situação
                </label>
                <Select
                  value={paymentSituationFilter}
                  onChange={(v) => setPaymentSituationFilter((v as PaymentSituationFilter) || "")}
                  options={PAYMENT_SITUATION_OPTIONS}
                  disabled={loading}
                  searchable={false}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-[#374151] space-y-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
                Período
              </label>
              <div className="flex flex-wrap gap-2">
                {DATE_FILTER_OPTIONS.map((opt) => (
                  <ActionButton
                    key={opt.value}
                    type="button"
                    size="xs"
                    variant={filterModal.periodDraft!.dateFilterType === opt.value ? "primary" : "outline"}
                    onClick={() => {
                      setPeriodFilterError(null);
                      patchPeriodDraft({ dateFilterType: opt.value });
                    }}
                    className={
                      filterModal.periodDraft!.dateFilterType === opt.value
                        ? ""
                        : "dark:border-[#4B5563] dark:bg-[#374151] dark:text-slate-300 dark:hover:bg-[#2D3748]"
                    }
                  >
                    {opt.label}
                  </ActionButton>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 flex-wrap items-end">
                  <div className="min-w-0 flex-1 min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-0.5">
                      De
                    </label>
                    <Input
                      type="date"
                      value={filterModal.periodDraft.dateRangeFrom}
                      onChange={(e) => {
                        setPeriodFilterError(null);
                        patchPeriodDraft({ dateRangeFrom: e.target.value });
                      }}
                      disabled={loading}
                      className="text-sm"
                    />
                  </div>
                  <div className="min-w-0 flex-1 min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-0.5">
                      Até
                    </label>
                    <Input
                      type="date"
                      value={filterModal.periodDraft.dateRangeTo}
                      onChange={(e) => {
                        setPeriodFilterError(null);
                        patchPeriodDraft({ dateRangeTo: e.target.value });
                      }}
                      disabled={loading}
                      className="text-sm"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 pb-0.5">
                    <ActionButton
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleClearPeriodDraft}
                      disabled={loading}
                      className="shrink-0 py-2.5 dark:border-[#4B5563] dark:bg-[#374151] dark:text-slate-200 dark:hover:bg-[#2D3748]"
                    >
                      Limpar período
                    </ActionButton>
                  </div>
                </div>
                {periodFilterError && (
                  <p className="text-xs text-red-600 dark:text-red-400">{periodFilterError}</p>
                )}
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Filtros por coluna valem na lista ao fechar o modal. Para o <strong>período</strong>, use{" "}
                  <strong>Aplicar filtros</strong> para buscar na API; ao cancelar, mudanças só no período são
                  descartadas.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-4 mt-4 border-t border-gray-200 dark:border-[#374151]">
              <ActionButton
                type="button"
                variant="outline"
                size="sm"
                onClick={closeFiltersModal}
                disabled={loading}
                className="dark:border-[#4B5563] dark:bg-[#374151] dark:text-slate-200 dark:hover:bg-[#2D3748]"
              >
                Cancelar
              </ActionButton>
              <ActionButton type="button" variant="primary" size="sm" onClick={applyFiltersModal} disabled={loading}>
                Aplicar filtros
              </ActionButton>
            </div>
          </>
        )}
      </Modal>

      <Card className="p-8">
        {transactions.length === 0 ? (
          <EmptyState
            icon={<IconsLarge.Transactions />}
            title="Nenhum lançamento encontrado"
            description="Ajuste o período ou os filtros (botão Filtros) e aplique de novo, ou cadastre um lançamento."
            actionText={onEmptyCreateOutput ? "Novo lançamento" : undefined}
            onAction={onEmptyCreateOutput}
            actionDisabled={loading}
          />
        ) : (
          <DataList<TransactionAttributes>
            data={filteredTransactions}
            columns={columns}
            actions={actions}
            keyExtractor={(t) => t.id}
            pageSize={10}
            emptyMessage={
              hasActiveFilters
                ? "Nenhuma transação corresponde aos filtros."
                : "Nenhuma transação encontrada"
            }
            loading={loading}
            onRowClick={(t) => onEdit(t.id)}
            selectable
            selectedKeys={selectedTransactionIds}
            onSelectionChange={(selectedKeys) => setSelectedTransactionIds(selectedKeys)}
            renderSelectionToolbar={
              onBulkEdit && onBulkDelete
                ? ({ selectedCount }) => (
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full min-w-[240px]">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                        <span className="text-sm font-semibold text-gray-700 dark:text-slate-200 uppercase tracking-wide">
                          <span className="text-teal-500 dark:text-[#63B3ED] mr-1.5" aria-hidden>
                            •
                          </span>
                          {selectedCount} selecionado{selectedCount !== 1 ? "s" : ""}
                        </span>
                        <ActionButton
                          type="button"
                          variant="teal"
                          size="xs"
                          icon={<Icons.Edit />}
                          onClick={onBulkEdit}
                          disabled={loading}
                        >
                          Editar
                        </ActionButton>
                      </div>
                      <ActionButton
                        type="button"
                        variant="roseSolid"
                        size="xs"
                        icon={<Icons.Delete />}
                        onClick={onBulkDelete}
                        disabled={loading}
                        className="shrink-0"
                      >
                        Excluir
                      </ActionButton>
                    </div>
                  )
                : undefined
            }
          />
        )}
      </Card>
    </div>
  );
};
